import React, { useState, useEffect, useRef } from "react";
import authFetch from "../authFetch";

/* ══ GROCERY LABEL ═══════════════════════════════════════════
   Unified bilingual renderer for any grocery row. Takes a row with
   any shape of { item | itemEn | itemAr | nameEn | nameAr } plus
   optional metadata and renders in the viewer's language.

   Resolution order for a given viewer lang:
   1. Prefer a stored counterpart (itemEn/itemAr, nameEn/nameAr).
   2. If the counterpart is missing and the stored text is in the
      OTHER language, lazy-translate via /api/ai-chat.
   3. Cache every translation in localStorage keyed by content hash
      so subsequent renders (or reloads) skip the API call.
   4. While translating, show the original with a subtle spinner.
      Never block the render.

   Bidirectional: works for EN→AR and AR→EN.

   Persistent cache is a simple JSON object at
   localStorage["cmdGroceryI18n"] keyed "<dir>:<text>" → translation.
   Capped at 2000 entries with naive FIFO eviction. */

const CACHE_KEY = "cmdGroceryI18n";
const CACHE_MAX = 2000;
const AR_RE = /[؀-ۿ]/;
const isArabic = (s) => typeof s === "string" && AR_RE.test(s);

function readCache() {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY) || "{}");
  } catch { return {}; }
}
function writeCache(obj) {
  try {
    const keys = Object.keys(obj);
    if (keys.length > CACHE_MAX) {
      const trimmed = {};
      keys.slice(-CACHE_MAX).forEach(k => { trimmed[k] = obj[k]; });
      obj = trimmed;
    }
    localStorage.setItem(CACHE_KEY, JSON.stringify(obj));
  } catch {}
}

/* In-flight translation tracker so we don't fire duplicate requests
   for the same string across multiple <GroceryLabel> instances. */
const pending = new Map(); // key → Promise<string>

async function translateOne(text, targetLang) {
  const dir = targetLang === "ar" ? "ar" : "en";
  const key = `${dir}:${text}`;
  const cache = readCache();
  if (cache[key] !== undefined) return cache[key];
  if (pending.has(key)) return pending.get(key);

  const p = (async () => {
    const targetName = dir === "ar" ? "Arabic" : "English";
    try {
      const r = await authFetch("/api/ai-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: 80,
          messages: [{
            role: "user",
            content: `Translate this grocery item name to ${targetName}. Respond with only the translation, no quotes, no extra text.\n\nItem: ${text}`
          }]
        })
      });
      const d = await r.json();
      const out = (d.content?.[0]?.text || "").trim().replace(/^["']|["']$/g, "");
      const next = readCache();
      next[key] = out || text;
      writeCache(next);
      return next[key];
    } catch {
      return text; // fail-silent: show original
    } finally {
      pending.delete(key);
    }
  })();
  pending.set(key, p);
  return p;
}

/* Resolve the best-available text for a given viewer lang.
   Schema priority:
     itemEn / itemAr  ← canonical bilingual fields (post-Batch-3 schema)
     nameEn / nameAr  ← catalog/intelligence-DB shape
     item   / itemAr  ← legacy schema (pre-Batch-3); item holds the
                       text in whatever language the user typed
   For the legacy shape we use Arabic-character detection to pin the
   stored text into the right slot so the resolver can detect when a
   counterpart is genuinely missing vs already populated. */
function resolve(row, lang) {
  if (!row) return { text: "", translated: false, src: null };
  const fields = {
    en: row.itemEn ?? row.nameEn ?? (isArabic(row.item) ? null : row.item) ?? (isArabic(row.name) ? null : row.name),
    ar: row.itemAr ?? row.nameAr ?? (isArabic(row.item) ? row.item : null) ?? (isArabic(row.name) ? row.name : null),
  };
  const want = lang === "ar" ? "ar" : "en";
  const other = want === "ar" ? "en" : "ar";
  if (fields[want]) return { text: fields[want], translated: false, src: null };
  if (fields[other]) return { text: fields[other], translated: false, src: other };
  const raw = row.item || row.name || "";
  return { text: raw, translated: false, src: isArabic(raw) ? "ar" : "en" };
}

export default function GroceryLabel({ row, lang, style, showOriginal = false }) {
  const targetLang = lang === "ar" ? "ar" : "en";
  const initial = resolve(row, targetLang);
  const [text, setText] = useState(initial.text);
  const [loading, setLoading] = useState(false);
  const [original, setOriginal] = useState(initial.src ? initial.text : null);
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => {
    const r = resolve(row, targetLang);
    if (!r.src) {
      /* Counterpart was available — no translation needed. */
      setText(r.text);
      setOriginal(null);
      return;
    }
    /* Need to translate from r.src language to targetLang. */
    const cached = readCache()[`${targetLang}:${r.text}`];
    if (cached) {
      setText(cached);
      setOriginal(showOriginal ? r.text : null);
      return;
    }
    setText(r.text);
    setOriginal(showOriginal ? r.text : null);
    setLoading(true);
    translateOne(r.text, targetLang).then(translated => {
      if (!mounted.current) return;
      setText(translated);
      setLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.itemEn, row?.itemAr, row?.nameEn, row?.nameAr, row?.item, row?.name, targetLang, showOriginal]);

  return (
    <span style={style}>
      {text}
      {loading && <span style={{fontSize:10,color:"var(--gold)",fontFamily:"var(--fm)",marginInlineStart:6}}>{targetLang==="ar"?"…ترجمة":"…"}</span>}
      {showOriginal && original && original !== text && (
        <span style={{fontSize:10,color:"var(--g400)",fontFamily:"var(--fm)",marginInlineStart:6}}>({original})</span>
      )}
    </span>
  );
}

/* Batch prefetch hook — for list views. Pre-fills the cache for all
   rows that will need translation so each GroceryLabel below renders
   instantly without its own API call. Safe to call every render;
   deduplicates internally. */
export function useGroceryPrefetch(rows, lang) {
  const targetLang = lang === "ar" ? "ar" : "en";
  const key = (rows || []).map(r => {
    const rr = resolve(r, targetLang);
    return rr.src ? `${targetLang}:${rr.text}` : "";
  }).filter(Boolean).join("|");

  useEffect(() => {
    if (!key) return;
    const cache = readCache();
    const needed = key.split("|").filter(k => k && cache[k] === undefined);
    if (!needed.length) return;
    /* Batch into one AI call for up to 20 items at a time. */
    const batch = needed.slice(0, 20);
    const items = batch.map(k => k.split(":").slice(1).join(":"));
    const targetName = targetLang === "ar" ? "Arabic" : "English";
    authFetch("/api/ai-chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "claude-haiku-4-5-20251001",
        max_tokens: 600,
        messages: [{
          role: "user",
          content: `Translate these grocery item names to ${targetName}. Return ONLY a JSON object where each key is the original and value is the translation. No markdown, no extra text.\n\nItems: ${JSON.stringify(items)}`
        }]
      })
    })
    .then(r => r.json())
    .then(d => {
      const raw = (d.content?.[0]?.text || "{}").replace(/```json|```/g, "").trim();
      try {
        const map = JSON.parse(raw);
        const next = readCache();
        Object.entries(map).forEach(([orig, tr]) => {
          if (typeof tr === "string" && tr.trim()) next[`${targetLang}:${orig}`] = tr.trim();
        });
        writeCache(next);
      } catch {}
    })
    .catch(() => {});
  }, [key, targetLang]);
}
